import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Card,
  CardContent,
  Grid,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  FormControlLabel,
  Collapse, Modal
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalculateIcon from '@mui/icons-material/Calculate';
import PersonIcon from '@mui/icons-material/Person';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { styled } from '@mui/material/styles';

import {useQueryAsUser} from '../clients/graphql/graphql.hooks';
import {
  portfolioPositionsGet,
  portfolioPositionHistoryGet,
  PositionHistory,
  PortfolioScope
} from '../clients/graphql/views/positions-queries';
import {useCalculatePositionHistorySequences} from "./users/calculatePositionHistorySequencesMutations";
import {CalculationCompleteModal} from "../components/CalculationCompleteModal";

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
}));

const ScopeChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
  fontWeight: 'bold',
}));

const getScopeColor = (type: number) => {
  switch (type) {
    case 7: return 'primary'; // PILOTED
    case 8: return 'secondary'; // MANUAL
    case 9: return 'success'; // ENTIRE_ACCOUNT
    default: return 'default';
  }
};

const getScopeLabel = (type: number) => {
  switch (type) {
    case 7: return 'PILOTED';
    case 8: return 'MANUAL';
    case 9: return 'ENTIRE_ACCOUNT';
    default: return `TYPE_${type}`;
  }
};

interface PositionRow {
  id: string;
  symbol: string;
  positionType: string;
  pilotedQuantity: number;
  manualQuantity: number;
  brokerageQuantity: number;
  totalQuantity: number;
  avgCostBasis: number;
  realizedPL: number;
  lastSequence: number;
  lastUpdated: string;
  position: any;
}

interface PositionDetailDialogProps {
  open: boolean;
  onClose: () => void;
  position: any;
  portfolioKey: number;
  userKey: number;
}

const PositionDetailDialog: React.FC<PositionDetailDialogProps> = ({
  open,
  onClose,
  position,
  portfolioKey,
  userKey,
}) => {
  const [showHistorical, setShowHistorical] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});
  
  const { data: historyData } = useQueryAsUser(
    portfolioPositionHistoryGet,
    {
      userKey,
      variables: {
        portfolioKey,
        types: ['PILOTED', 'MANUAL', 'ENTIRE_ACCOUNT'],
        positionKey: position?.positionKey
      },
      skip: !open || !position
    }
  );

  const scopeData = useMemo(() => {
    if (!historyData?.portfolioGet?.portfolio?.portfolioScope) return {};
    
    console.log('Position Detail Dialog - Raw scope data:', historyData.portfolioGet.portfolio.portfolioScope);
    
    return historyData.portfolioGet.portfolio.portfolioScope.reduce((acc: any, scope: PortfolioScope) => {
      const scopeType = getScopeLabel(scope.type);
      console.log(`Scope type ${scope.type} mapped to ${scopeType}, has ${scope.positionHistory?.length || 0} positions`);
      
      if (scopeType === 'PILOTED') {
        // For PILOTED, group by autopilot settings key
        const key = `PILOTED_${scope.autoPilotSettingsKey || 'UNKNOWN'}`;
        if (acc[key]) {
          acc[key].histories = [...acc[key].histories, ...scope.positionHistory];
        } else {
          acc[key] = {
            histories: [...scope.positionHistory],
            autoPilotSettingsKey: scope.autoPilotSettingsKey,
            type: 'PILOTED'
          };
        }
        // Sort by sequence number after combining
        acc[key].histories.sort((a: PositionHistory, b: PositionHistory) => b.sequenceNumber - a.sequenceNumber);
      } else {
        // For non-PILOTED, use the original logic
        if (acc[scopeType]) {
          acc[scopeType].histories = [...acc[scopeType].histories, ...scope.positionHistory];
        } else {
          acc[scopeType] = {
            histories: [...scope.positionHistory],
            autoPilotSettingsKey: scope.autoPilotSettingsKey,
            type: scopeType
          };
        }
        // Sort by sequence number after combining
        acc[scopeType].histories.sort((a: PositionHistory, b: PositionHistory) => b.sequenceNumber - a.sequenceNumber);
      }
      
      return acc;
    }, {});
  }, [historyData]);

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  if (!position) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            {position.symbol} Position Details
          </Typography>
          <Stack direction="row" spacing={1}>
            <FormControlLabel
              control={
                <Switch
                  checked={showHistorical}
                  onChange={(e) => setShowHistorical(e.target.checked)}
                />
              }
              label="Show Historical"
            />
            <IconButton onClick={onClose}>×</IconButton>
          </Stack>
        </Stack>
      </DialogTitle>
      
      <DialogContent>
        <Grid container spacing={3}>
          {/* Position Summary */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Position Summary</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">Symbol</Typography>
                    <Typography variant="h6">{position.symbol}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">Type</Typography>
                    <Typography variant="h6">{position.positionType}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">Asset Key</Typography>
                    <Typography variant="h6">{position.assetKey}</Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="body2" color="text.secondary">Position Key</Typography>
                    <Typography variant="h6">{position.positionKey}</Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Scope Breakdown */}
          {Object.entries(scopeData)
            .filter(([sectionKey, scopeInfo]: [string, any]) => {
              const histories = scopeInfo.histories || [];
              return histories.length > 0; // Only show sections with actual position data
            })
            .sort(([a], [b]) => {
              // Sort order: ENTIRE_ACCOUNT first, then MANUAL, then PILOTED_* groups
              if (a === 'ENTIRE_ACCOUNT') return -1;
              if (b === 'ENTIRE_ACCOUNT') return 1;
              if (a === 'MANUAL') return -1;
              if (b === 'MANUAL') return 1;
              return a.localeCompare(b);
            })
            .map(([sectionKey, scopeInfo]: [string, any]) => {
            const histories = scopeInfo.histories || [];
            const autoPilotSettingsKey = scopeInfo.autoPilotSettingsKey;
            const scopeType = scopeInfo.type;
            
            // Generate display label
            let displayLabel = '';
            if (sectionKey === 'ENTIRE_ACCOUNT') {
              displayLabel = 'ENTIRE ACCOUNT';
            } else if (sectionKey === 'MANUAL') {
              displayLabel = 'MANUAL';
            } else if (sectionKey.startsWith('PILOTED_')) {
              const settingsKey = sectionKey.replace('PILOTED_', '');
              displayLabel = `PILOTED Positions - AutopilotSettingsKey ${settingsKey}`;
            } else {
              displayLabel = sectionKey;
            }
            
            const displayedHistories = showHistorical ? histories : histories.slice(0, 1);
            const isPiloted = scopeType === 'PILOTED';
            
            // Helper function to get pilot name (temporarily using autopilot settings key)
            const getPilotName = (autoPilotSettingsKey?: number) => {
              if (!autoPilotSettingsKey) return 'N/A';
              return `Pilot ${autoPilotSettingsKey}`;
            };
            
            // Set default expanded state for new sections
            if (expandedSections[sectionKey] === undefined) {
              setExpandedSections(prev => ({ ...prev, [sectionKey]: true }));
            }
            
            console.log(`Rendering ${sectionKey}: ${histories.length} histories, ${displayedHistories.length} displayed`);
            
            return (
              <Grid item xs={12} key={sectionKey}>
                <Card>
                  <CardContent>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        cursor: 'pointer',
                        mb: 1
                      }}
                      onClick={() => toggleSection(sectionKey)}
                    >
                      <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ScopeChip 
                          label={scopeType === 'PILOTED' ? 'PILOTED' : scopeType === 'MANUAL' ? 'MANUAL' : 'ENTIRE ACCOUNT'} 
                          color={getScopeColor(scopeType === 'PILOTED' ? 7 : scopeType === 'MANUAL' ? 8 : 9)}
                          size="small"
                        />
                        {displayLabel}
                        {scopeType === 'PILOTED' && autoPilotSettingsKey && (
                          <Button
                            size="small"
                            component={Link}
                            to={`/pilot-details/${autoPilotSettingsKey}`}
                            onClick={(e) => e.stopPropagation()}
                            sx={{ 
                              minWidth: 'auto',
                              padding: '2px 8px',
                              fontSize: '0.75rem',
                              textTransform: 'none'
                            }}
                          >
                            View Pilot
                          </Button>
                        )}
                        <Chip 
                          label={histories.length} 
                          size="small" 
                          variant="filled"
                          sx={{ 
                            backgroundColor: 'primary.light',
                            color: 'primary.contrastText',
                            fontWeight: 'bold',
                            minWidth: '32px',
                            height: '20px'
                          }}
                        />
                      </Typography>
                      <IconButton size="small">
                        {expandedSections[sectionKey] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </Box>
                    
                    <Collapse in={expandedSections[sectionKey]}>
                      {histories.length > 0 ? (
                        <Box>
                          <TableContainer 
                            sx={{ 
                              maxHeight: 400, 
                              overflow: 'auto',
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1
                            }}
                          >
                            <Table size="small" stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ backgroundColor: 'background.paper', zIndex: 1 }}>Sequence</TableCell>
                                  <TableCell align="right" sx={{ backgroundColor: 'background.paper', zIndex: 1 }}>Quantity</TableCell>
                                  <TableCell align="right" sx={{ backgroundColor: 'background.paper', zIndex: 1 }}>Avg Cost Basis</TableCell>
                                  <TableCell align="right" sx={{ backgroundColor: 'background.paper', zIndex: 1 }}>Realized P/L</TableCell>
                                  <TableCell align="right" sx={{ backgroundColor: 'background.paper', zIndex: 1 }}>Unaccounted</TableCell>
                                  {isPiloted && (
                                    <TableCell sx={{ backgroundColor: 'background.paper', zIndex: 1 }}>Order Key</TableCell>
                                  )}
                                  <TableCell sx={{ backgroundColor: 'background.paper', zIndex: 1 }}>Last Updated</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {displayedHistories.map((history: PositionHistory) => (
                                  <TableRow key={history.positionHistoryKey} hover>
                                    <TableCell>{history.sequenceNumber}</TableCell>
                                    <TableCell align="right">{history.totalNetQuantity.toFixed(4)}</TableCell>
                                    <TableCell align="right">${history.averageUnitCostBasis.toFixed(2)}</TableCell>
                                    <TableCell align="right" sx={{ color: history.cumulativeRealizedProfitLoss >= 0 ? 'success.main' : 'error.main' }}>
                                      ${history.cumulativeRealizedProfitLoss.toFixed(2)}
                                    </TableCell>
                                    <TableCell align="right">{history.unaccountedForShares.toFixed(4)}</TableCell>
                                    {isPiloted && (
                                      <TableCell>
                                        {history.orderKey || 'N/A'}
                                      </TableCell>
                                    )}
                                    <TableCell>{new Date(history.computedAt).toLocaleString()}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </TableContainer>
                          
                          {!showHistorical && histories.length > 1 && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Showing latest entry. Enable "Show Historical" to see all {histories.length} entries.
                            </Typography>
                          )}
                          
                          {showHistorical && (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              Showing all {displayedHistories.length} entries (scroll to see more)
                            </Typography>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                          No {displayLabel.toLowerCase()} positions found
                        </Typography>
                      )}
                    </Collapse>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export const PortfolioPositionsPage: React.FC = () => {
  const { portfolioKey, userKey } = useParams<{ portfolioKey: string; userKey: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('ENTIRE_ACCOUNT');
  const [selectedPosition, setSelectedPosition] = useState<any>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [calculationCompleteModalOpen, setCalculationCompleteModalOpen] = useState(false);

  const { data, loading, error } = useQueryAsUser(
    portfolioPositionsGet,
    {
      userKey: parseInt(userKey || '0'),
      variables: {
        portfolioKey: parseInt(portfolioKey || '0'),
        types: [selectedType] as any[]
      }
    }
  );

  const { calculatePositionHistorySequencesAPI } = useCalculatePositionHistorySequences(userKey, portfolioKey)

  const calculatePositionHistorySequences = (positionKey: string) => {
    console.log("calculatePositionHistorySequences", positionKey)
    return calculatePositionHistorySequencesAPI(positionKey).then(x => {
      console.log("calculatePositionHistorySequencesAPI done", x)
    }).catch(err => {
      console.error("calculatePositionHistorySequencesAPI error", err)
    })
  }

  const processedData = useMemo(() => {
    if (!data?.portfolioGet?.portfolio?.portfolioScope) return [];

    console.log('Main table - Raw scope data:', data.portfolioGet.portfolio.portfolioScope);
    console.log('Selected type:', selectedType);

    // Since we're only showing one type at a time, process the single scope
    const scope = data.portfolioGet.portfolio.portfolioScope[0];
    if (!scope) return [];

    const scopeType = getScopeLabel(scope.type);
    console.log(`Main table - Scope type ${scope.type} mapped to ${scopeType}`);
    
    return scope.positionHistory.map((history: PositionHistory) => ({
      id: history.position.symbol,
      symbol: history.position.symbol,
      positionType: history.position.positionType,
      pilotedQuantity: scopeType === 'PILOTED' ? history.totalNetQuantity : 0,
      manualQuantity: scopeType === 'MANUAL' ? history.totalNetQuantity : 0,
      brokerageQuantity: scopeType === 'ENTIRE_ACCOUNT' ? history.totalNetQuantity : 0,
      totalQuantity: history.totalNetQuantity,
      avgCostBasis: history.averageUnitCostBasis,
      realizedPL: history.cumulativeRealizedProfitLoss,
      lastSequence: history.sequenceNumber,
      lastUpdated: history.computedAt,
      position: history.position
    }));
  }, [data, selectedType]);

  const filteredData = useMemo(() => {
    if (!searchTerm) return processedData;
    return processedData.filter((row: PositionRow) => 
      row.symbol.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [processedData, searchTerm]);

  // Handle URL symbol parameter to open modal directly
  useEffect(() => {
    const symbolParam = searchParams.get('symbol');
    if (symbolParam && processedData.length > 0 && !detailDialogOpen) {
      const position = processedData.find((row: PositionRow) => 
        row.symbol.toLowerCase() === symbolParam.toLowerCase()
      );
      if (position) {
        setSelectedPosition(position.position);
        setDetailDialogOpen(true);
      }
    }
  }, [searchParams, processedData, detailDialogOpen]);

  const handleCloseModal = () => {
    setDetailDialogOpen(false);
    setSelectedPosition(null);
    // Remove symbol parameter from URL when modal closes
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('symbol');
    setSearchParams(newSearchParams);
  };

  const columns: GridColDef[] = [
    { 
      field: 'symbol', 
      headerName: 'Symbol', 
      width: 100,
      flex: 0.8,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {params.value}
        </Typography>
      )
    },
    { 
      field: 'positionType', 
      headerName: 'Type', 
      width: 100,
      flex: 0.8,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" />
      )
    },
    { 
      field: 'totalQuantity', 
      headerName: 'Quantity', 
      width: 120, 
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {params.value.toFixed(4)}
        </Typography>
      )
    },
    { 
      field: 'avgCostBasis', 
      headerName: 'Avg Cost', 
      width: 120, 
      flex: 1,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => `$${params.value.toFixed(2)}`
    },
    { 
      field: 'realizedPL', 
      headerName: 'Realized P/L', 
      width: 130, 
      flex: 1.2,
      align: 'right',
      headerAlign: 'right',
      renderCell: (params) => (
        <Typography 
          variant="body2" 
          color={params.value >= 0 ? 'success.main' : 'error.main'}
        >
          ${params.value.toFixed(2)}
        </Typography>
      )
    },
    { 
      field: 'lastSequence', 
      headerName: 'Sequence', 
      width: 80, 
      flex: 0.6,
      align: 'right',
      headerAlign: 'right'
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 80,
      flex: 0.6,
      sortable: false,
      renderCell: (params) => (
          <>
              <Tooltip title="View Position Details">
                  <IconButton
                      size="small"
                      onClick={() => {
                          setSelectedPosition(params.row.position);
                          setDetailDialogOpen(true);
                          // Add symbol to URL when modal opens
                          const newSearchParams = new URLSearchParams(searchParams);
                          newSearchParams.set('symbol', params.row.symbol);
                          setSearchParams(newSearchParams);
                      }}
                  >
                      <VisibilityIcon/>
                  </IconButton>
              </Tooltip>
              <Tooltip title="Recalculate">
                  <IconButton
                      size="small"
                      onClick={() => {
                        console.log("Clicked Recalculate button", params.row.position.positionKey)
                        calculatePositionHistorySequences(params.row.position.positionKey).then(() => {
                          setCalculationCompleteModalOpen(true)
                        })
                      }}
                  >
                      <CalculateIcon/>
                  </IconButton>
              </Tooltip>
          </>
      )
    }
  ];

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">Error loading positions: {error.message}</Alert>;

  const portfolio = data?.portfolioGet?.portfolio;

  return (
    <>
    <Box sx={{ p: 3 }}>
      <StyledPaper>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 3 }}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Portfolio Positions
            </Typography>
            
            {portfolio && (
              <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <Typography variant="h6">
                  {portfolio.nickname} ({portfolio.brokerName})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Portfolio Key: {portfolio.portfolioKey}
                </Typography>
              </Stack>
            )}
          </Box>
          
          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<PersonIcon />}
              component="a"
              href={`/users/${userKey}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/users/${userKey}`);
              }}
              size="small"
            >
              View User
            </Button>
            <Button
              variant="outlined"
              startIcon={<AccountBalanceIcon />}
              component="a"
              href={`/portfolios/${portfolioKey}`}
              onClick={(e) => {
                e.preventDefault();
                navigate(`/portfolios/${portfolioKey}`);
              }}
              size="small"
            >
              Portfolio Details
            </Button>
          </Stack>
        </Stack>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder="Search by symbol..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Position Type</InputLabel>
              <Select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value as string)}
                renderValue={(selected) => (
                  <ScopeChip
                    label={selected === 'ENTIRE_ACCOUNT' ? 'ENTIRE ACCOUNT' : selected}
                    color={getScopeColor(selected === 'PILOTED' ? 7 : selected === 'MANUAL' ? 8 : 9)}
                    size="small"
                  />
                )}
              >
                <MenuItem value="ENTIRE_ACCOUNT">Entire Account</MenuItem>
                <MenuItem value="MANUAL">Manual</MenuItem>
                <MenuItem value="PILOTED">Piloted</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={filteredData}
            columns={columns}
            loading={loading}
            disableRowSelectionOnClick
            hideFooter={filteredData.length <= 100}
            sx={{
              '& .MuiDataGrid-cell': {
                borderBottom: '1px solid',
                borderBottomColor: 'divider',
                display: 'flex',
                alignItems: 'center',
              },
              '& .MuiDataGrid-row:hover': {
                backgroundColor: 'action.hover',
              }
            }}
          />
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Showing {filteredData.length} positions
          </Typography>
        </Box>
      </StyledPaper>

      <PositionDetailDialog
        open={detailDialogOpen}
        onClose={handleCloseModal}
        position={selectedPosition}
        portfolioKey={parseInt(portfolioKey || '0')}
        userKey={parseInt(userKey || '0')}
      />
    </Box>
    <CalculationCompleteModal open={calculationCompleteModalOpen} onClose={() => setCalculationCompleteModalOpen(false)}/>
    </>
  );
};